(() => {
  'use strict';

  const controllerKey = '__avStableHeaderController';
  window[controllerKey]?.destroy?.();

  const lifecycleAbort = new AbortController();
  let currentHeader = null;
  let cleanupHeader = () => {};
  let cleanupOffset = () => {};

  const lifecycleOn = (target, type, handler, options = {}) => {
    target?.addEventListener(type, handler, { ...options, signal: lifecycleAbort.signal });
  };

  const initHeader = (header) => {
    if (!header) return () => {};
    header.classList.remove('av-header--no-js');

    const abortController = new AbortController();
    const timeouts = new Set();
    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const inertRecords = new Map();
    let activeModal = null;

    const on = (target, type, handler, options = {}) => {
      target?.addEventListener(type, handler, { ...options, signal: abortController.signal });
    };

    const syncCartCount = (event) => {
      const count = Number(event.detail?.cartCount);
      if (!Number.isInteger(count) || count < 0) return;

      header.querySelectorAll('[data-av-cart-count-link]').forEach((link) => {
        const noun = count === 1
          ? link.dataset.avCartItemOne
          : link.dataset.avCartItemOther;

        link.setAttribute('aria-label', [
          link.dataset.avCartName,
          link.dataset.avCartWith,
          String(count),
          noun
        ].filter(Boolean).join(' '));

        let badge = link.querySelector('.av-header-cart__count');
        if (count === 0) {
          badge?.remove();
          return;
        }

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'av-header-cart__count';
          badge.setAttribute('aria-hidden', 'true');
          link.append(badge);
        }

        badge.textContent = count > 9 ? '9+' : String(count);
      });
    };

    on(document, 'theme:cart:change', syncCartCount);

    const after = (handler, delay) => {
      const timeout = window.setTimeout(() => {
        timeouts.delete(timeout);
        handler();
      }, delay);
      timeouts.add(timeout);
      return timeout;
    };

    const restoreBackground = () => {
      inertRecords.forEach((record, element) => {
        element.inert = record.inert;
        if (record.hadAriaHidden) element.setAttribute('aria-hidden', record.ariaHidden);
        else element.removeAttribute('aria-hidden');
      });
      inertRecords.clear();
      activeModal = null;
    };

    const isolateModal = (modal) => {
      if (!modal || activeModal === modal) return;
      restoreBackground();

      let branch = modal;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;

        [...parent.children].forEach((sibling) => {
          if (sibling === branch || !(sibling instanceof HTMLElement) || sibling.matches('script,style,link')) return;
          inertRecords.set(sibling, {
            inert: sibling.inert,
            hadAriaHidden: sibling.hasAttribute('aria-hidden'),
            ariaHidden: sibling.getAttribute('aria-hidden') || ''
          });
          sibling.inert = true;
          sibling.setAttribute('aria-hidden', 'true');
        });

        branch = parent;
      }

      activeModal = modal;
    };

    const releaseModal = (modal) => {
      if (activeModal === modal) restoreBackground();
    };

    const mega = header.querySelector('[data-av-mega-menu]');
    const megaTriggers = [...header.querySelectorAll('[data-av-mega-trigger]')];
    const megaPanels = [...header.querySelectorAll('[data-av-nav-group-panel]')];
    const megaInner = header.querySelector('[data-av-mega-inner]');
    const megaFeatures = [...header.querySelectorAll('[data-av-mega-feature-group]')];
    let megaTimer;
    let activeMegaTrigger;
    let restoringMegaFocus = false;

    const setMegaState = (expanded, trigger) => {
      megaTriggers.forEach((item) => item.setAttribute('aria-expanded', String(expanded && item === trigger)));
    };

    const setActiveMegaGroup = (trigger) => {
      const group = trigger?.dataset.avNavGroup || 'comprar';
      mega?.setAttribute('data-av-active-group', group);
      megaPanels.forEach((panel) => {
        const active = panel.dataset.avNavGroupPanel === group;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      let activeFeature = null;
      megaFeatures.forEach((feature) => {
        const active = feature.dataset.avMegaFeatureGroup === group;
        feature.hidden = !active;
        feature.setAttribute('aria-hidden', String(!active));
        if (active) activeFeature = feature;
      });
      const groupOnly = !activeFeature;
      megaInner?.classList.toggle('av-mega__inner--group-only', groupOnly);
      mega?.classList.toggle('av-mega--group-only', groupOnly);
    };

    const openMega = (trigger) => {
      if (!mega) return;
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      activeMegaTrigger = trigger || activeMegaTrigger;
      setActiveMegaGroup(activeMegaTrigger);
      mega.hidden = false;
      setMegaState(true, activeMegaTrigger);
      document.documentElement.classList.add('av-mega-is-open');
      header.classList.add('av-header--mega-open');
    };

    const scheduleMegaOpen = (trigger, delay = 80) => {
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      megaTimer = after(() => openMega(trigger), delay);
    };

    const closeMega = (delay = 220, restoreFocus = false) => {
      if (!mega) return;
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      megaTimer = after(() => {
        mega.hidden = true;
        setMegaState(false, activeMegaTrigger);
        document.documentElement.classList.remove('av-mega-is-open');
        header.classList.remove('av-header--mega-open');
        if (restoreFocus && activeMegaTrigger?.isConnected) {
          restoringMegaFocus = true;
          activeMegaTrigger.focus();
          restoringMegaFocus = false;
        }
      }, delay);
    };

    megaTriggers.forEach((trigger) => {
      on(trigger, 'pointerenter', () => scheduleMegaOpen(trigger));
      on(trigger, 'focusin', () => {
        if (restoringMegaFocus) return;
        openMega(trigger);
      });
      on(trigger, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openMega(trigger);
        window.requestAnimationFrame(() => {
          mega?.querySelector('[data-av-nav-group-panel]:not([hidden]) a[href]')?.focus();
        });
      });
      on(trigger, 'click', (event) => {
        const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!coarsePointer || (!mega?.hidden && activeMegaTrigger === trigger)) return;
        event.preventDefault();
        openMega(trigger);
      });
    });
    on(mega, 'pointerenter', () => openMega(activeMegaTrigger));
    on(mega, 'pointerleave', () => closeMega());
    on(mega, 'focusin', () => openMega(activeMegaTrigger));
    on(header, 'pointerleave', () => closeMega());
    on(document, 'click', (event) => {
      if (mega && !mega.hidden && !header.contains(event.target)) closeMega(0);
    });

    const trapFocus = (event, root) => {
      if (event.key !== 'Tab' || !root || root.hidden) return;
      const nodes = [...root.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null || element === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const search = header.querySelector('[data-av-search-panel]');
    const searchOpeners = [...header.querySelectorAll('[data-av-search-open]')];
    const searchClose = header.querySelector('[data-av-search-close]');
    const mobile = header.querySelector('[data-av-mobile-drawer]');
    const mobileOpen = header.querySelector('[data-av-mobile-open]');
    const mobileClose = header.querySelector('[data-av-mobile-close]');
    let searchLast;
    let mobileLast;

    const setSearchState = (expanded) => {
      searchOpeners.forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
      search?.setAttribute('aria-hidden', String(!expanded));
    };

    const setMobileState = (expanded) => {
      mobileOpen?.setAttribute('aria-expanded', String(expanded));
      mobile?.setAttribute('aria-hidden', String(!expanded));
    };

    const closeSearch = ({ restoreFocus = true } = {}) => {
      if (!search) return;
      const wasOpen = !search.hidden;
      search.hidden = true;
      setSearchState(false);
      document.body.classList.remove('av-search-open');
      releaseModal(search);
      if (wasOpen && restoreFocus && searchLast?.isConnected) searchLast.focus();
    };

    const closeMobile = ({ restoreFocus = true } = {}) => {
      if (!mobile) return;
      const wasOpen = !mobile.hidden;
      mobile.hidden = true;
      setMobileState(false);
      document.body.classList.remove('av-mobile-menu-open');
      releaseModal(mobile);
      mobile.scrollTop = 0;
      mobile.querySelectorAll('[data-av-mobile-group][open]').forEach((group) => group.removeAttribute('open'));
      if (wasOpen && restoreFocus && mobileLast?.isConnected) mobileLast.focus();
    };

    const openSearch = () => {
      if (!search) return;
      searchLast = document.activeElement;
      closeMobile({ restoreFocus: false });
      closeMega(0);
      search.hidden = false;
      setSearchState(true);
      document.body.classList.add('av-search-open');
      isolateModal(search);
      after(() => search.querySelector('[data-av-search-input]')?.focus(), 30);
    };

    const openMobile = () => {
      if (!mobile) return;
      mobileLast = document.activeElement;
      closeSearch({ restoreFocus: false });
      closeMega(0);
      mobile.hidden = false;
      setMobileState(true);
      document.body.classList.add('av-mobile-menu-open');
      isolateModal(mobile);
      after(() => mobile.querySelector('input,button,a')?.focus(), 30);
    };

    setSearchState(false);
    setMobileState(false);

    searchOpeners.forEach((button) => {
      on(button, 'click', openSearch);
    });
    on(searchClose, 'click', () => closeSearch());
    on(search, 'click', (event) => {
      if (event.target === search) closeSearch();
    });
    on(search, 'keydown', (event) => trapFocus(event, search));

    on(mobileOpen, 'click', openMobile);
    on(mobileClose, 'click', () => closeMobile());
    on(mobile, 'keydown', (event) => trapFocus(event, mobile));
    mobile?.querySelectorAll('[data-av-mobile-group]').forEach((group) => {
      on(group, 'toggle', () => {
        if (!group.open) return;
        mobile.querySelectorAll('[data-av-mobile-group][open]').forEach((other) => {
          if (other !== group) other.removeAttribute('open');
        });
        group.scrollIntoView({ block: 'nearest' });
      });
    });

    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (mega && !mega.hidden) closeMega(0, true);
      closeSearch();
      closeMobile();
    });

    return () => {
      abortController.abort();
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      timeouts.clear();
      window.clearTimeout(megaTimer);
      restoreBackground();
      document.body.classList.remove('av-search-open', 'av-mobile-menu-open');
      document.documentElement.classList.remove('av-mega-is-open');
      header.classList.remove('av-header--mega-open');
      setSearchState(false);
      setMobileState(false);
    };
  };

  const initStickyOffset = (header) => {
    const root = document.documentElement;
    if (!header) {
      root.style.setProperty('--av-header-sticky-offset', '0px');
      return () => {};
    }

    const abortController = new AbortController();
    const selectors = '[data-announcement-bar],[class*="announcement"],[class*="utility-bar"],[class*="support-bar"],[class*="top-bar"],[class*="topbar"]';
    let frame = 0;
    let observer;
    const candidates = () => [...document.querySelectorAll(selectors)].filter((node) => node !== header && !node.contains(header) && !header.contains(node));
    const update = () => {
      frame = 0;
      let offset = 0;
      candidates().forEach((node) => {
        const style = getComputedStyle(node);
        const rect = node.getBoundingClientRect();
        if (style.display === 'none' || style.visibility === 'hidden' || rect.height <= 0 || rect.height > 96 || rect.bottom <= 0 || rect.top > 96) return;
        const fixedAtTop = (style.position === 'fixed' || style.position === 'sticky') && rect.top <= 2;
        if (fixedAtTop || rect.top >= -2) offset = Math.max(offset, Math.round(rect.bottom));
      });
      root.style.setProperty('--av-header-sticky-offset', `${Math.max(0, offset)}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true, signal: abortController.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: abortController.signal });
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(schedule);
      candidates().forEach((node) => observer.observe(node));
      observer.observe(header);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', update, { once: true, signal: abortController.signal });
    else update();

    return () => {
      abortController.abort();
      observer?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  };

  const refresh = () => {
    cleanupHeader();
    cleanupOffset();
    currentHeader = document.querySelector('[data-av-header]');
    cleanupHeader = initHeader(currentHeader);
    cleanupOffset = initStickyOffset(currentHeader);
  };

  const destroy = () => {
    cleanupHeader();
    cleanupOffset();
    lifecycleAbort.abort();
    currentHeader = null;
  };

  window[controllerKey] = { destroy, refresh };
  refresh();

  lifecycleOn(document, 'shopify:section:load', (event) => {
    const root = event.target;
    const containsHeader = root?.matches?.('[data-av-header]') || root?.querySelector?.('[data-av-header]');
    if (containsHeader || !currentHeader?.isConnected) refresh();
  });

  lifecycleOn(document, 'shopify:section:unload', (event) => {
    if (!currentHeader || !(event.target === currentHeader || event.target?.contains?.(currentHeader))) return;
    cleanupHeader();
    cleanupOffset();
    cleanupHeader = () => {};
    cleanupOffset = () => {};
    currentHeader = null;
  });
})();
